/**
 * Coordinate Format Parser
 *
 * Supports parsing coordinates in multiple formats:
 * - Decimal Degrees (DD): 40.7128, -74.0060
 * - Degrees Minutes Seconds (DMS): 40°42'46"N 74°00'21"W
 * - Degrees Decimal Minutes (DDM): 40° 42.767'N 74° 0.35'W
 * - Google Maps URLs
 */

export interface ParsedCoordinates {
  latitude: number
  longitude: number
}

export type CoordinateFormat = 'DD' | 'DMS' | 'DDM' | 'URL' | 'UNKNOWN'

/**
 * Main entry point - attempts to parse coordinates from any supported format
 */
export function parseCoordinates(input: string): ParsedCoordinates | null {
  if (!input || typeof input !== 'string') {
    return null
  }

  // Normalize input: trim, normalize whitespace
  const normalized = normalizeInput(input)

  // Try parsing in order of specificity

  // 1. Try Google Maps URL format
  const urlResult = parseGoogleMapsURL(normalized)
  if (urlResult) return urlResult

  // 2. Try DMS (most specific)
  const dmsResult = parseDMSFormat(normalized)
  if (dmsResult) return dmsResult

  // 3. Try DDM
  const ddmResult = parseDDMFormat(normalized)
  if (ddmResult) return ddmResult

  // 4. Try DD (least specific)
  const ddResult = parseDDFormat(normalized)
  if (ddResult) return ddResult

  return null
}

/**
 * Detect which format the input appears to be
 */
export function detectFormat(input: string): CoordinateFormat {
  const normalized = normalizeInput(input)

  if (/^https?:\/\//i.test(normalized)) {
    return 'URL'
  }

  // DMS: contains degree symbol and quotes/apostrophes or multiple numbers with spaces
  if (/\d+[°º]\s*\d+['′]\s*\d+(?:\.\d+)?["″]/i.test(normalized)) {
    return 'DMS'
  }

  // DDM: contains degree symbol and decimal minutes
  if (/\d+[°º]\s*\d+\.\d+['′]/i.test(normalized)) {
    return 'DDM'
  }

  // DD: simple decimal numbers
  if (/^[+-]?\d+\.?\d*\s*[,;]\s*[+-]?\d+\.?\d*$/i.test(normalized)) {
    return 'DD'
  }

  return 'UNKNOWN'
}

/**
 * Normalize input string: trim, remove invisible characters, normalize whitespace
 */
function normalizeInput(input: string): string {
  return (
    input
      .trim()
      // Remove BOM and other invisible characters
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      // Normalize multiple whitespace to single space
      .replace(/\s+/g, ' ')
  )
}

/**
 * Parse Decimal Degrees format
 * Examples: 40.7128, -74.0060 | 40.7128 N, 74.0060 W | -40.7128, 74.0060
 */
function parseDDFormat(input: string): ParsedCoordinates | null {
  // Pattern for DD: optional sign, digits, optional decimal, optional hemisphere
  // MUST be separated by comma or semicolon to distinguish from single values
  const pattern = /^\s*([+-]?\d+\.?\d*)\s*[°º]?\s*([NSEW])?\s*[,;]\s*([+-]?\d+\.?\d*)\s*[°º]?\s*([NSEW])?\s*$/i

  const match = input.match(pattern)
  if (!match) return null

  let lat = parseFloat(match[1])
  const latHemisphere = match[2]?.toUpperCase()
  let lon = parseFloat(match[3])
  const lonHemisphere = match[4]?.toUpperCase()

  // Apply hemisphere
  if (latHemisphere === 'S') lat = -Math.abs(lat)
  if (latHemisphere === 'N') lat = Math.abs(lat)
  if (lonHemisphere === 'W') lon = -Math.abs(lon)
  if (lonHemisphere === 'E') lon = Math.abs(lon)

  // Validate
  if (!isValidCoordinate(lat, lon)) {
    return null
  }

  return { latitude: lat, longitude: lon }
}

/**
 * Parse Degrees Minutes Seconds format
 * Examples: 40°42'46"N 74°00'21"W | 40 42 46 N, 74 0 21 W | N40°42'46" W74°00'21"
 */
function parseDMSFormat(input: string): ParsedCoordinates | null {
  // Match DMS components: degree symbol or whitespace separates degrees from minutes
  const dmsPattern = /([NSEW])?\s*(\d+)(?:\s*[°º]\s*|\s+)(\d+)\s*(?:['′]\s*|\s+)(\d+(?:\.\d+)?)\s*["″]?([NSEW])?/gi

  const matches = [...input.matchAll(dmsPattern)]
  if (matches.length < 2) return null

  // Extract all hemisphere letters from the full input in order
  const hemiMatches = [...input.matchAll(/[NSEW]/gi)].map(m => m[0].toUpperCase())

  // Assign hemispheres: first N/S goes to lat coord, first E/W goes to lon coord
  let firstHemi: string | undefined
  let secondHemi: string | undefined
  for (const h of hemiMatches) {
    if ((h === 'N' || h === 'S') && !firstHemi) firstHemi = h
    else if ((h === 'E' || h === 'W') && !secondHemi) secondHemi = h
    else if (!firstHemi) firstHemi = h
    else if (!secondHemi) secondHemi = h
  }

  const coords: number[] = []
  const matchHemis = [firstHemi, secondHemi]

  for (let i = 0; i < 2; i++) {
    const match = matches[i]
    const degrees = parseInt(match[2], 10)
    const minutes = parseInt(match[3], 10)
    const seconds = parseFloat(match[4])

    if (isNaN(degrees) || isNaN(minutes) || isNaN(seconds)) {
      return null
    }

    if (minutes >= 60 || seconds >= 60) {
      return null
    }

    const decimal = dmsToDecimal(degrees, minutes, seconds, matchHemis[i])
    coords.push(decimal)
  }

  const [first, second] = coords

  // Determine lat/lon order based on hemisphere
  let lat: number, lon: number

  if (firstHemi === 'N' || firstHemi === 'S') {
    lat = first
    lon = second
  } else if (firstHemi === 'E' || firstHemi === 'W') {
    lat = second
    lon = first
  } else {
    lat = first
    lon = second
  }

  if (!isValidCoordinate(lat, lon)) {
    return null
  }

  return { latitude: lat, longitude: lon }
}

/**
 * Parse Degrees Decimal Minutes format
 * Examples: 40° 42.767'N 74° 0.35'W | 40 42.767 N, 74 0.35 W
 */
function parseDDMFormat(input: string): ParsedCoordinates | null {
  // Pattern for DDM: degrees and decimal minutes (must have decimal point to distinguish from DMS)
  const ddmPattern = /([NSEW])?\s*(\d+)[°º\s]+(\d+\.\d+)['′]?\s*([NSEW])?/gi

  const matches = [...input.matchAll(ddmPattern)]
  if (matches.length < 2) return null

  // Extract all hemisphere letters from the full input in order
  const hemiMatches = [...input.matchAll(/[NSEW]/gi)].map(m => m[0].toUpperCase())

  let firstHemi: string | undefined
  let secondHemi: string | undefined
  for (const h of hemiMatches) {
    if ((h === 'N' || h === 'S') && !firstHemi) firstHemi = h
    else if ((h === 'E' || h === 'W') && !secondHemi) secondHemi = h
    else if (!firstHemi) firstHemi = h
    else if (!secondHemi) secondHemi = h
  }

  const coords: number[] = []
  const matchHemis = [firstHemi, secondHemi]

  for (let i = 0; i < 2; i++) {
    const match = matches[i]
    const degrees = parseInt(match[2], 10)
    const decimalMinutes = parseFloat(match[3])

    if (isNaN(degrees) || isNaN(decimalMinutes)) {
      return null
    }

    if (decimalMinutes >= 60) {
      return null
    }

    const decimal = ddmToDecimal(degrees, decimalMinutes, matchHemis[i])
    coords.push(decimal)
  }

  const [first, second] = coords

  let lat: number, lon: number

  if (firstHemi === 'N' || firstHemi === 'S') {
    lat = first
    lon = second
  } else if (firstHemi === 'E' || firstHemi === 'W') {
    lat = second
    lon = first
  } else {
    lat = first
    lon = second
  }

  if (!isValidCoordinate(lat, lon)) {
    return null
  }

  return { latitude: lat, longitude: lon }
}

/**
 * Parse Google Maps URL
 * Examples:
 * - https://www.google.com/maps?q=40.7128,-74.0060
 * - https://www.google.com/maps/place/@40.7128,-74.0060,15z
 * - https://maps.app.goo.gl/... (short links redirect, extract from q param)
 */
function parseGoogleMapsURL(input: string): ParsedCoordinates | null {
  try {
    // Check if it looks like a URL
    if (!/^https?:\/\//i.test(input)) {
      return null
    }

    const url = new URL(input)

    // Try query parameter 'q'
    const qParam = url.searchParams.get('q')
    if (qParam) {
      const coords = qParam.split(',').map(s => parseFloat(s.trim()))
      if (coords.length === 2 && coords.every(c => !isNaN(c))) {
        const [lat, lon] = coords
        if (isValidCoordinate(lat, lon)) {
          return { latitude: lat, longitude: lon }
        }
      }
    }

    // Try extracting from path (e.g., /place/@lat,lon,zoom)
    const pathMatch = url.pathname.match(/@([+-]?\d+\.?\d*),([+-]?\d+\.?\d*)/)
    if (pathMatch) {
      const lat = parseFloat(pathMatch[1])
      const lon = parseFloat(pathMatch[2])
      if (isValidCoordinate(lat, lon)) {
        return { latitude: lat, longitude: lon }
      }
    }

    return null
  } catch {
    return null
  }
}

/**
 * Convert DMS to Decimal Degrees
 */
function dmsToDecimal(degrees: number, minutes: number, seconds: number, hemisphere?: string): number {
  const dd = degrees + minutes / 60 + seconds / 3600

  // Apply hemisphere
  if (hemisphere === 'S' || hemisphere === 'W') {
    return -Math.abs(dd)
  }

  return Math.abs(dd)
}

/**
 * Convert DDM to Decimal Degrees
 */
function ddmToDecimal(degrees: number, decimalMinutes: number, hemisphere?: string): number {
  const dd = degrees + decimalMinutes / 60

  // Apply hemisphere
  if (hemisphere === 'S' || hemisphere === 'W') {
    return -Math.abs(dd)
  }

  return Math.abs(dd)
}

/**
 * Validate coordinate ranges
 */
export function isValidCoordinate(latitude: number, longitude: number): boolean {
  return (
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180
  )
}
